// src/utils/auth.js
import axios from 'axios';

const isDev = process.env.NODE_ENV === 'development';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://fcn-api-platform-hzcfayfsc0gebfhw.centralus-01.azurewebsites.net';
const PORTAL_URL = process.env.REACT_APP_PORTAL_URL || 'https://aplicacionesfcn.com';
const APP_ID = process.env.REACT_APP_APLICACION_ID || '12';

export const getCurrentToken = () => {
  if (isDev && process.env.REACT_APP_SESSION_ID) {
    return process.env.REACT_APP_SESSION_ID;
  }

  const cookies = document.cookie.split('; ');
  const sessionIdCookie = cookies.find(row => row.startsWith('sessionId='));
  
  if (sessionIdCookie) {
    return sessionIdCookie.split('=')[1];
  }

  const localSessionId = localStorage.getItem('sessionId');
  if (localSessionId) {
    return localSessionId;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const sessionIdFromUrl = urlParams.get('sessionId');
  if (sessionIdFromUrl) {
    localStorage.setItem('sessionId', sessionIdFromUrl);
    window.history.replaceState({}, document.title, window.location.pathname);
    return sessionIdFromUrl;
  }

  return null;
};

const Autenticacion = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

Autenticacion.interceptors.request.use(
  (config) => {
    if (!config.url?.includes('/auth/')) {
      config.headers['X-Aplicacion-Id'] = APP_ID;
    }

    const sessionId = getCurrentToken();
    
    if (sessionId) {
      config.headers['Authorization'] = `Bearer ${sessionId}`;

      config.headers['SessionId'] = sessionId; // Header adiciona
      console.log('🔐 sessionId agregado en headers');
    } else {
      console.warn('⚠️ No hay sessionId disponible para esta petición');
      
      if (!isDev) {
        console.log('🔄 No hay sesión, redirigiendo al portal...');
        window.location.href = PORTAL_URL;
        return Promise.reject(new Error('No session available'));
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

Autenticacion.interceptors.response.use(
  (response) => {
    if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
      throw new Error('API returned HTML instead of JSON - possible authentication issue');
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('sessionId');
      sessionStorage.clear();
      if (!isDev) {
        window.location.href = PORTAL_URL;
      }
    }
    return Promise.reject(error);
  }
);

export const verifySession = async () => {
  try {
    const sessionId = getCurrentToken();
    if (!sessionId) {
      if (!isDev) {
        window.location.href = PORTAL_URL;
      }
      return {
        success: false,
        error: 'No hay sessionId disponible',
        needsAuth: true
      };
    }

    const response = await Autenticacion.get('/auth/verify-session');
    const { data } = response;

    if (data.success && data.valid && data.user) {
      return {
        success: true,
        user: data.user,
        permissions: data.permissions || data.permisos || []
      };
    } else {
      localStorage.removeItem('sessionId');
      if (!isDev) {
        window.location.href = PORTAL_URL;
      }
      return {
        success: false,
        error: 'Sesión inválida',
        needsAuth: true
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Error de conexión',
      needsAuth: error.response?.status === 401
    };
  }
};

export const logoutUser = () => {
  sessionStorage.clear();
  localStorage.clear();
  document.cookie = 'sessionId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.aplicacionesfcn.com';
  window.location.href = PORTAL_URL;
  return true;
};

export default Autenticacion;
